'use client';

import { useState } from "react";
import Link from "next/link";
import type { Transition } from "framer-motion";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { Placeholder } from "@/components/taller/placeholder";
import { TallerButtonLink } from "@/components/taller/button";
import { useTranslations } from "@/i18n/client";
import type { CaseItem } from "@/lib/contentful-types";

interface Props {
  item: CaseItem;
  idx: number;
  prev: CaseItem | null;
  next: CaseItem | null;
}

const TIPO_COLORS: Record<string, string> = {
  corporativa: "#04D4CF",
  restaurante: "#FF9F5A",
  artista:     "#8A4FFF",
  seguridad:   "#4ADE80",
};

const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];
function tr(delay = 0): Transition { return { delay, duration: 0.6, ease: EASE }; }
function isExternalUrl(url: string): boolean { return /^https?:\/\//i.test(url); }

// ── Contentful markdown parser ────────────────────────────────
interface ResumenSection { label: string; body: string; }

function parseSections(text: string): ResumenSection[] {
  // Regex que busca __Texto:__ o __Texto__ como títulos de sección
  const re = /__([^_\n]+?)__[:]?/g;
  const matches = [...text.matchAll(re)];

  if (matches.length === 0) return [{ label: "", body: text.trim() }];

  return matches.map((m, i) => {
    const start = (m.index ?? 0) + m[0].length;
    const end =
      i + 1 < matches.length
        ? (matches[i + 1].index ?? text.length)
        : text.length;

    // Limpiamos el cuerpo de la sección (listas, saltos de línea innecesarios)
    const body = text
      .slice(start, end)
      .replace(/^[-*+]\s+/gm, "• ") // Opcional: convertir guiones en puntos
      .trim();

    return { label: m[1].trim(), body };
  });
}

function RichBody({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") ? (
          <strong key={i} className="font-semibold text-[color:var(--color-ink)]">
            {p.slice(2, -2)}
          </strong>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

const SECTION_ICONS: Record<string, string> = {
  "El Reto":      "01",
  "Reto":         "01",
  "La Solución":  "02",
  "Solución":     "02",
  "El Resultado": "03",
  "Resultado":    "03",
};

function RichResumen({ text, accent }: { text: string; accent: string }) {
  const sections = parseSections(text);
  if (sections.length === 1 && !sections[0].label) {
    return (
      <p className="font-sans text-[16px] md:text-[18px] leading-[1.7] text-[color:var(--color-mute)] m-0">
        <RichBody text={text} />
      </p>
    );
  }
  return (
    <div className="space-y-4">
      {sections.map(({ label, body }, i) => {
        const num = SECTION_ICONS[label] ?? String(i + 1).padStart(2, "0");
        return (
          <div
            key={i}
            className="relative rounded-xl p-5"
            style={{
              background: `${accent}07`,
              borderLeft: `2px solid ${accent}50`,
            }}
          >
            <div
              className="flex items-center gap-2 mb-2.5"
            >
              <span
                className="font-mono text-[9px] w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `${accent}20`, color: accent }}
              >
                {num}
              </span>
              <span
                className="font-mono text-[10px] tracking-[0.22em] uppercase font-semibold"
                style={{ color: accent }}
              >
                {label}
              </span>
            </div>
            <p className="font-sans text-[15px] md:text-[16px] leading-[1.72] text-[color:var(--color-mute)] m-0">
              <RichBody text={body} />
            </p>
          </div>
        );
      })}
    </div>
  );
}

export function CasoDetailContent({ item, idx, prev, next }: Props) {
  const { t, isLoading } = useTranslations();
  const cd = t('casoDetail');
  const [zoomImg, setZoomImg] = useState<{ url: string; alt: string } | null>(null);

  if (isLoading) return null;

  const accent = TIPO_COLORS[item.tipo] ?? "#04D4CF";

  return (
    <>
      {/* ── LIGHTBOX ───────────────────────────────────────── */}
      <AnimatePresence>
        {zoomImg && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-6 md:p-12 cursor-zoom-out"
            onClick={() => setZoomImg(null)}
          >
            <button
              className="absolute top-5 right-7 text-white/40 hover:text-white text-4xl font-light transition-colors leading-none"
              onClick={() => setZoomImg(null)}
              aria-label="Cerrar"
            >
              ×
            </button>
            <motion.img
              initial={{ scale: 0.93, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.28, ease: EASE }}
              src={zoomImg.url}
              alt={zoomImg.alt}
              className="max-w-full max-h-full object-contain select-none rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── TOP BAR ────────────────────────────────────────── */}
      <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-7 pb-2">
        <motion.div
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={tr(0)}
        >
          <Link
            href="/casos-exito"
            className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.16em] uppercase text-[color:var(--color-mute)] no-underline hover:text-[color:var(--color-accent)] transition-colors group"
          >
            <span className="transition-transform duration-300 group-hover:-translate-x-1 inline-block"></span>
            {cd.back}
          </Link>
        </motion.div>
      </div>

      {/* ── HERO HEADER ────────────────────────────────────── */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-8 pb-10 relative overflow-hidden">
        {/* ambient glow */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-16 -left-16 w-[360px] h-[260px] rounded-full blur-[100px] opacity-[0.09]"
          style={{ background: accent }}
        />

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={tr(0.05)}
          className="font-mono text-[11px] tracking-[0.24em] uppercase mb-5 relative"
          style={{ color: accent }}
        >
          {item.kind}
        </motion.p>

        <div className="flex flex-wrap items-center gap-4 relative">
          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={tr(0.1)}
            className="font-sans font-bold tracking-tight text-[52px] md:text-[76px] lg:text-[92px] leading-[0.88] text-[color:var(--color-ink)] m-0"
          >
            {item.client}
          </motion.h1>
          <motion.span
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={tr(0.2)}
            className="font-mono text-[10px] tracking-[0.18em] uppercase rounded-full px-4 py-1.5 shrink-0"
            style={{
              color: accent,
              border: `1px solid ${accent}55`,
              background: `${accent}10`,
            }}
          >
            {item.tag}
          </motion.span>
        </div>
      </section>

      {/* ── HERO IMAGES (responsive stacking without cropping) ────────── */}
      {item.imagenPrincipal && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={tr(0.15)}
          className="max-w-[var(--container-max)] mx-auto px-8 md:px-14 mb-14"
        >
          {/* Images layout: Column on mobile, side-by-side on md+ */}
          <div className={`flex flex-col md:flex-row gap-6 items-center md:items-start justify-center`}>
            {/* Primary Image (Hidden on mobile) */}
            <div
              className={`relative cursor-zoom-in group flex-1 w-full justify-center hidden md:flex`}
              onClick={() =>
                setZoomImg({
                  url: item.imagenPrincipal!.url,
                  alt: item.imagenPrincipal!.alt || item.client,
                })
              }
            >
              <Image
                src={item.imagenPrincipal.url}
                width={item.imagenPrincipal.width}
                height={item.imagenPrincipal.height}
                alt={item.imagenPrincipal.alt || item.client}
                sizes="(max-width: 1024px) 100vw, 70vw"
                className="max-w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02] rounded-lg"
                priority
              />
              {/* zoom hint */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div
                  className="font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full backdrop-blur-md"
                  style={{ background: `${accent}30`, color: accent, border: `1px solid ${accent}50` }}
                >
                  zoom
                </div>
              </div>
            </div>

            {/* Secondary Image (Visible on all, centered on mobile) */}
            {(item.imagenSecundaria || !item.imagenPrincipal) && (
              <div
                className="relative cursor-zoom-in group w-full sm:w-3/4 md:w-auto md:max-w-[24%] lg:max-w-[22%] flex justify-center"
                onClick={() => {
                  const imgUrl = item.imagenSecundaria ? item.imagenSecundaria.url : item.imagenPrincipal!.url;
                  const imgAlt = item.imagenSecundaria ? (item.imagenSecundaria.alt || item.client) : (item.imagenPrincipal!.alt || item.client);
                  setZoomImg({ url: imgUrl, alt: imgAlt });
                }}
              >
                <Image
                  src={item.imagenSecundaria ? item.imagenSecundaria.url : item.imagenPrincipal!.url}
                  width={item.imagenSecundaria ? item.imagenSecundaria.width : item.imagenPrincipal!.width}
                  height={item.imagenSecundaria ? item.imagenSecundaria.height : item.imagenPrincipal!.height}
                  alt={item.imagenSecundaria ? (item.imagenSecundaria.alt || item.client) : (item.imagenPrincipal!.alt || item.client)}
                  sizes="(max-width: 768px) 100vw, (max-width: 1024px) 25vw, 22vw"
                  className="max-w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02] rounded-lg"
                  // If we are falling back to primary image on mobile because secondary doesn't exist, we need to show it.
                  // The container logic above handles the main hiding, but we handle the fallback here.
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div
                    className="font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 rounded-full backdrop-blur-md"
                    style={{ background: `${accent}30`, color: accent, border: `1px solid ${accent}50` }}
                  >
                    zoom
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.section>
      )}

      {/* Fallback placeholder when no image */}
      {!item.imagenPrincipal && (
        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 mb-14">
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              height: item.tall,
              background: `linear-gradient(135deg, ${accent}18, ${accent}05)`,
              border: `1px solid ${accent}20`,
            }}
          >
            <Placeholder label={item.client} height={item.tall} width="100%" dark={idx % 2 === 1} />
          </div>
        </div>
      )}

      {/* ── MAIN CONTENT: 2-COL ────────────────────────────── */}
      <section className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-14 lg:gap-20 items-start">

          {/* LEFT: text */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={tr(0.22)}
            >
              <RichResumen text={item.resumen} accent={accent} />
            </motion.div>

            {/* Specs */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={tr(0.3)}
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 pt-10 border-t border-[color:var(--color-border)]"
            >
              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-2 m-0">
                  {cd.sidebar.duration}
                </p>
                <p className="font-sans font-semibold text-[20px] text-[color:var(--color-ink)] m-0">
                  {item.duracion}
                </p>
              </div>

              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-2 m-0">
                  {cd.sidebar.type}
                </p>
                <p className="font-mono text-[13px] tracking-[0.1em] uppercase font-semibold m-0" style={{ color: accent }}>
                  {item.tipo}
                </p>
              </div>

              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-3 m-0">
                  {cd.sidebar.stack}
                </p>
                <div className="flex flex-wrap gap-2">
                  {item.stack.map((tech) => (
                    <span
                      key={tech}
                      className="font-mono text-[10px] tracking-[0.06em] rounded-md px-2.5 py-1"
                      style={{
                        color: accent,
                        border: `1px solid ${accent}30`,
                        background: `${accent}08`,
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-3 m-0">
                  {cd.sidebar.deliverables}
                </p>
                <ul className="list-none p-0 m-0 space-y-2">
                  {item.entregables.map((ent) => (
                    <li key={ent} className="flex items-start gap-2.5 font-sans text-[14px] text-[color:var(--color-ink)] leading-[1.45]">
                      <span className="font-mono text-[11px] shrink-0 mt-px" style={{ color: accent }}>✓</span>
                      {ent}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {/* RIGHT: visual accent card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={tr(0.18)}
            className="lg:sticky lg:top-14"
          >
            {/* Project number badge */}
            <div
              className="rounded-2xl p-8 flex flex-col gap-6"
              style={{
                background: `linear-gradient(135deg, ${accent}10, ${accent}04)`,
                border: `1px solid ${accent}20`,
              }}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)]">
                  Proyecto #{String(idx + 1).padStart(2, "0")}
                </div>
                {item.ctaUrl ? (
                  <TallerButtonLink
                    variant="secondary"
                    size="sm"
                    href={item.ctaUrl}
                    external={isExternalUrl(item.ctaUrl)}
                    className="w-fit"
                  >
                    Visitar la web
                  </TallerButtonLink>
                ) : null}
              </div>

              {/* big accent number / duration */}
              <div>
                <div
                  className="font-sans font-bold text-[64px] leading-none tracking-tight"
                  style={{ color: accent }}
                >
                  {item.duracion.replace(/\s/g, "\u00A0")}
                </div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--color-mute)] mt-2">
                  {cd.sidebar.duration}
                </div>
              </div>

              <hr style={{ borderColor: `${accent}20` }} />

              {/* Entregables list */}
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-4">
                  {cd.sidebar.deliverables}
                </div>
                <ul className="list-none p-0 m-0 space-y-3">
                  {item.entregables.map((ent) => (
                    <li
                      key={ent}
                      className="flex items-center gap-3 font-sans text-[14px] text-[color:var(--color-ink)]"
                    >
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] shrink-0"
                        style={{ background: `${accent}20`, color: accent }}
                      >
                        ✓
                      </span>
                      {ent}
                    </li>
                  ))}
                </ul>
              </div>

              <hr style={{ borderColor: `${accent}20` }} />

              {/* Stack */}
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-3">
                  {cd.sidebar.stack}
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.stack.map((tech) => (
                    <span
                      key={tech}
                      className="font-mono text-[10px] tracking-[0.06em] rounded-md px-2.5 py-1"
                      style={{
                        color: accent,
                        border: `1px solid ${accent}35`,
                        background: `${accent}10`,
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CASE NAVIGATION ────────────────────────────────── */}
      {(prev || next) && (
        <nav className="border-t border-[color:var(--color-border)]">
          <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-12 grid grid-cols-2 gap-8">
            <div>
              {prev && (
                <Link href={`/casos-exito/${prev.slug}`} className="group flex flex-col no-underline">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-3 flex items-center gap-2">
                    <span className="transition-transform duration-300 group-hover:-translate-x-1 inline-block"></span>
                    {cd.nav.prev}
                  </span>
                  <span className="font-sans font-[350] text-[20px] tracking-[-0.01em] text-[color:var(--color-ink)] group-hover:text-[color:var(--color-accent)] transition-colors">
                    {prev.client}
                  </span>
                </Link>
              )}
            </div>
            <div className="text-right">
              {next && (
                <Link href={`/casos-exito/${next.slug}`} className="group flex flex-col items-end no-underline">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[color:var(--color-mute)] mb-3 flex items-center justify-end gap-2">
                    {cd.nav.next}
                    <span className="transition-transform duration-300 group-hover:translate-x-1 inline-block"></span>
                  </span>
                  <span className="font-sans font-[350] text-[20px] tracking-[-0.01em] text-[color:var(--color-ink)] group-hover:text-[color:var(--color-accent)] transition-colors">
                    {next.client}
                  </span>
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-32 border-t border-[color:var(--color-border)]">
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[560px] h-[240px] rounded-full blur-[100px] opacity-[0.12]"
          style={{ background: `radial-gradient(ellipse, ${accent}, #8A4FFF)` }}
        />

        <div className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 relative">
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-10 lg:gap-16 items-end">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-sans font-bold text-[44px] md:text-[60px] lg:text-[68px] leading-[0.93] tracking-[-0.025em] m-0 text-[color:var(--color-ink)]"
            >
              {cd.cta.headingPart1}
              <span style={{ color: accent }}>{cd.cta.headingAccent}</span>?
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.14, ease: EASE }}
              className="space-y-7"
            >
              <p className="font-sans text-[17px] md:text-[19px] leading-[1.55] text-[color:var(--color-mute)] m-0">
                {cd.cta.subtitle}
              </p>
              <div>
                {item.ctaLabel && item.ctaUrl ? (
                  <TallerButtonLink variant="primary" href={item.ctaUrl}>
                    {item.ctaLabel}
                  </TallerButtonLink>
                ) : (
                  <TallerButtonLink variant="primary" href="/contacto">
                    {cd.cta.button}
                  </TallerButtonLink>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}
